import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { PrimaryBtn } from 'components/UI/Buttons';
import { type FC, useState } from 'react';
import type { SurveyConfig } from 'services/types/followUpConfig';

interface SurveyConfigFormProps {
  surveyConfig: SurveyConfig | null;
  onSurveyConfigChange: (surveyConfig: SurveyConfig | null) => void;
  disabled: boolean;
  /** When true, the survey is required: the activate/deactivate toggle is hidden and the
   *  survey name is marked as required. The caller is responsible for providing a non-null
   *  surveyConfig in this mode. */
  required?: boolean;
}

const SurveyConfigForm: FC<SurveyConfigFormProps> = ({
  surveyConfig,
  onSurveyConfigChange,
  disabled = false,
  required = false,
}) => {
  const [newButtonTitle, setNewButtonTitle] = useState('');

  const handleSurveyNameChange = (surveyName: string) => {
    if (!surveyConfig) {
      onSurveyConfigChange({
        surveyName,
        question: '',
        buttons: [],
      });
    } else {
      onSurveyConfigChange({
        ...surveyConfig,
        surveyName,
      });
    }
  };

  const handleQuestionChange = (question: string) => {
    if (!surveyConfig) {
      onSurveyConfigChange({
        surveyName: '',
        question,
        buttons: [],
      });
    } else {
      onSurveyConfigChange({
        ...surveyConfig,
        question,
      });
    }
  };

  const handleAddButton = () => {
    if (!newButtonTitle.trim()) return;

    const newButton = { title: newButtonTitle.trim() };
    const updatedButtons = [...(surveyConfig?.buttons || []), newButton];

    if (!surveyConfig) {
      onSurveyConfigChange({
        surveyName: '',
        question: '',
        buttons: updatedButtons,
      });
    } else {
      onSurveyConfigChange({
        ...surveyConfig,
        buttons: updatedButtons,
      });
    }

    setNewButtonTitle('');
  };

  const handleRemoveButton = (index: number) => {
    if (!surveyConfig?.buttons) return;

    const updatedButtons = surveyConfig.buttons.filter((_, i) => i !== index);
    onSurveyConfigChange({
      ...surveyConfig,
      buttons: updatedButtons,
    });
  };

  const handleEnableSurvey = () => {
    onSurveyConfigChange({
      surveyName: '',
      question: '',
      buttons: [],
    });
  };

  const handleDisableSurvey = () => {
    onSurveyConfigChange(null);
  };

  return (
    <Box
      sx={{
        mt: 4,
        mb: 2,
        p: 2,
        border: 1,
        borderColor: '#c9d9c9',
        borderRadius: 2,
        bgcolor: '#f5f5f5',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h6" gutterBottom>
          {required ? 'Configuración de Encuesta' : 'Configuración de Encuesta (Opcional)'}
        </Typography>
        {!required &&
          (surveyConfig ? (
            <Button variant="outlined" color="error" onClick={handleDisableSurvey} disabled={disabled}>
              Desactivar Encuesta
            </Button>
          ) : (
            <Button variant="outlined" color="primary" onClick={handleEnableSurvey} disabled={disabled}>
              <AddIcon sx={{ mr: 1 }} />
              Activar Encuesta
            </Button>
          ))}
      </Box>
      {surveyConfig && (
        <>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Si activas esta opción, después de enviar el mensaje del follow-up se creará automáticamente una encuesta
            con la pregunta especificada. Si no agregas una pregunta, el follow-up creará una encuesta con solamente el
            título. La encuesta se asociará al último lead del cliente.
          </Typography>

          <Grid
            container
            spacing={2}
            sx={{
              mt: 1,
            }}
          >
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                required={required}
                error={required && !surveyConfig.surveyName.trim()}
                label="Nombre de la encuesta"
                value={surveyConfig.surveyName}
                onChange={(e) => handleSurveyNameChange(e.target.value)}
                disabled={disabled}
                placeholder="Ej: Encuesta de satisfacción"
                helperText={
                  required && !surveyConfig.surveyName.trim()
                    ? 'El nombre de la encuesta es obligatorio'
                    : 'Nombre identificativo para la encuesta'
                }
              />
            </Grid>

            <Grid size={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Pregunta de la encuesta"
                value={surveyConfig.question}
                onChange={(e) => handleQuestionChange(e.target.value)}
                disabled={disabled}
                placeholder="Ej: ¿Cómo calificarías nuestro servicio del 1 al 10?"
                helperText="La pregunta que se enviará al cliente"
              />
            </Grid>

            <Grid size={12}>
              <Typography variant="subtitle2" gutterBottom>
                Botones de respuesta (Opcional)
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Agrega botones de respuesta rápida para la primera pregunta de la encuesta.
              </Typography>

              {surveyConfig.buttons && surveyConfig.buttons.length > 0 && (
                <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                  {surveyConfig.buttons.map((button, index) => (
                    <Chip
                      key={`button-${button.title}`}
                      label={button.title}
                      onDelete={disabled ? undefined : () => handleRemoveButton(index)}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Stack>
              )}

              {!disabled && (
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                  }}
                >
                  <TextField
                    size="small"
                    label="Título del botón"
                    value={newButtonTitle}
                    onChange={(e) => setNewButtonTitle(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddButton();
                      }
                    }}
                    placeholder="Ej: Excelente"
                  />
                  <PrimaryBtn
                    variant="contained"
                    size="small"
                    onClick={handleAddButton}
                    disabled={!newButtonTitle.trim()}
                  >
                    <AddIcon />
                  </PrimaryBtn>
                </Box>
              )}
            </Grid>
          </Grid>

          <Box
            sx={{
              mt: 2,
              p: 2,
              bgcolor: '#e3f2fd',
              borderRadius: 1,
            }}
          >
            <Typography variant="caption" color="textSecondary">
              <strong>Nota:</strong> La encuesta se creará solo si el mensaje del follow-up se envía exitosamente. Si ya
              existe una encuesta con el mismo nombre para el cliente en los últimos 7 días, se agregará la nueva
              pregunta a la encuesta existente.
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};

export default SurveyConfigForm;
